import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Building2, Send, Gavel, UserPlus, Copy, MapPin, CalendarDays, Search } from "lucide-react";
import { PageHeader } from "@/components/control/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  Section, Loadable, Empty, Status, Info, History, ActionButton, RunButton, BackLink, SelectField, PageView, PageButtons, useLoad, fmt, fmtDay, can,
} from "@/components/control/kit";

const chip = "rounded-full bg-[#EEEAF7] px-3 py-1 text-xs font-bold text-brand-purple";

function OrgActivityCard({ a }) {
  return (
    <article className="flex flex-col gap-2 rounded-2xl border border-[#E6E1F0] bg-white p-5" data-testid={`org-activity-card-${a.id}`}>
      <h2 className="text-xl font-bold text-brand-purple">{a.title}</h2>
      <p className="text-sm text-brand-purple">{fmtDay(a.date)}{a.start && ` · ${a.start}–${a.end}`}</p>
      {a.location && <p className="text-sm text-zinc-600">{a.location}</p>}
      <div className="flex flex-wrap items-center gap-2">
        <Status value={a.status} />
        {a.capacity > 0 && <span className={chip} data-testid={`org-activity-seats-${a.id}`}>{a.confirmed} von {a.capacity} Plätzen bestätigt</span>}
      </div>
      {a.summary && <p className="line-clamp-2 text-sm text-zinc-600">{a.summary}</p>}
      <Link to={`/aktivitaeten/${a.id}`} data-testid={`org-activity-open-${a.id}`} aria-label={`Details ansehen: ${a.title}`}
        className="mt-auto inline-flex h-11 items-center justify-center rounded-lg bg-brand-magenta px-6 font-bold text-white hover:bg-brand-magenta-dark">Details ansehen</Link>
    </article>
  );
}

function OrgActivityCards({ o, acts, back }) {
  const [q, setQ] = useState("");
  const term = q.trim().toLowerCase();
  const shown = acts.filter((a) => `${a.title} ${a.location || ""}`.toLowerCase().includes(term));
  return (
    <>
      <label className="flex items-center gap-2 rounded-2xl border border-[#E6E1F0] bg-white px-4 py-3">
        <Search className="h-4 w-4 text-zinc-500" aria-hidden="true" />
        <span className="sr-only">Aktivität oder öffentlichen Ort suchen</span>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Aktivität oder öffentlichen Ort suchen …"
          className="w-full bg-transparent text-sm outline-none" data-testid="org-activities-view-search" />
      </label>
      <div className="flex flex-wrap gap-2" data-testid="org-activities-view-chips">
        <span className={chip}>{o.name}</span>{o.city && <span className={chip}>{o.city}</span>}<span className={chip} data-testid="org-activities-view-count">{term ? `${shown.length} von ${acts.length}` : acts.length} Aktivitäten</span>
      </div>
      {shown.length === 0 ? <Empty text={acts.length ? "Keine Aktivität passt zur Suche." : "Für diese Organisation sind noch keine Aktivitäten erfasst."} /> : (
        <div className="grid gap-4 md:grid-cols-2" data-testid="org-activities-view-list">{shown.map((a) => <OrgActivityCard key={a.id} a={a} />)}</div>
      )}
      <PageButtons back={back} testId="org-activities-view" />
    </>
  );
}

// C70: own page listing only this organisation's activities (data from the scoped organisation detail).
function OrgActivitiesPage({ o, acts }) {
  return (
    <PageView page="aktivitaeten" label="Als Ansicht öffnen" icon={CalendarDays} testId="org-activities-view" wide
      title="Aktivitäten einer Organisation" subtitle="Diese Ansicht umfasst nur die zugeordnete Organisation.">
      {({ back }) => <OrgActivityCards o={o} acts={acts} back={back} />}
    </PageView>
  );
}

function OrgLocations({ o }) {
  const { user } = useAuth();
  const st = useLoad(`/organizations/${o.id}/locations`);
  return (
    <Section title="Standorte und Zuständigkeit" testId="org-locations" actions={<>
      <Link to={`/organisationen/${o.id}/rechte`} data-testid="org-roles-link" className="self-center text-sm font-semibold text-brand-purple underline">Organisationsrollen</Link>
      {can(user, "organizations:manage") && (
        <ActionButton label="Standort hinzufügen" icon={MapPin} testId="add-location" onDone={st.reload} submitLabel="Standort speichern"
          page="standort" title="Standort hinzufügen" pageSubtitle="Erfasse einen öffentlichen Standort. Zugangsangaben bleiben bis zum Nachweis unbekannt."
          description="Neue Standorte beginnen mit unbekannten Zugangsangaben, bis eine Quelle vorliegt."
          fields={[{ name: "name", label: "Name des Standorts", required: true }, { name: "address", label: "Adresse" }, { name: "city", label: "Ort", default: o.city }]}
          onSubmit={async (v) => { await api.post(`/organizations/${o.id}/locations`, v); return "Standort angelegt."; }} />
      )}
    </>}>
      <Loadable state={st}>
        {(d) => (d.locations.length === 0 ? <Empty text="Noch keine Standorte erfasst." testId="org-locations-empty" /> : (
          <ul className="divide-y divide-border">
            {d.locations.map((l) => (
              <li key={l.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5">
                <Link to={`/organisationen/${o.id}/standorte/${l.id}`} data-testid={`org-location-${l.id}`} className="font-medium text-brand-purple hover:underline">{l.name}</Link>
                <span className="flex items-center gap-2 text-xs text-muted-foreground">{l.responsible_name || "Zuständigkeit offen"} <Status value={l.status} /></span>
              </li>
            ))}
          </ul>
        ))}
      </Loadable>
    </Section>
  );
}

function InviteLink({ url }) {
  return (
    <div className="space-y-2" data-testid="invite-url-box">
      <p className="text-sm">Der Einladungslink wird nur jetzt angezeigt. Gib ihn sicher an die eingeladene Person weiter (E-Mail-Versand ist nicht eingerichtet).</p>
      <code className="block break-all rounded bg-secondary p-2 text-xs" data-testid="invite-url">{url}</code>
      <button type="button" onClick={() => navigator.clipboard?.writeText(url)} data-testid="copy-invite-url" className="inline-flex items-center gap-1 text-sm font-medium text-brand-purple underline">
        <Copy className="h-4 w-4" /> Link kopieren
      </button>
    </div>
  );
}

export function inviteFields(roles) {
  return [
    { name: "first_name", label: "Vorname", required: true },
    { name: "email", label: "E-Mail-Adresse", type: "email", required: true },
    { name: "role", label: "Rolle", type: "select", required: true, options: roles },
    { name: "days", label: "Gültigkeit", type: "select", default: "7", placeholder: false, options: [3, 7, 14].map((x) => ({ value: String(x), label: `${x} Tage` })) },
    { name: "message", label: "Nachricht", type: "textarea" },
  ];
}

export const showInvite = (data) => ({ message: "Einladung erstellt.", show: <InviteLink url={`${window.location.origin}/einladung?token=${data.invite_url.split("token=")[1]}`} /> });

function Actions({ d, reload }) {
  const { user } = useAuth();
  const o = d.organization;
  return (
    <>
      <Status value={o.status} testId="org-status" />
      {["entwurf", "rückfrage"].includes(o.status) && (
        <RunButton label="Zur Prüfung senden" icon={Send} testId="submit-org" success="Zur Prüfung gesendet." onDone={reload} run={() => api.post(`/organizations/${o.id}/submit`)} />
      )}
      {o.status === "zur_pruefung" && (
        <ActionButton label="Angaben prüfen" icon={Gavel} variant="default" testId="decide-org" onDone={reload} title="Organisationsangaben prüfen"
          page="pruefen" pageSubtitle="Prüfe selbst angegebene Informationen und offene Zuständigkeit." submitLabel="Entscheidung speichern" pageSubject={o.name}
          pageRows={[["Organisation", o.name], ["Beschreibung", o.description], ["Quelle", o.source || "Nicht angegeben"], ["Stand", fmt(o.updated_at)],
            ["Erfasst durch", o.created_by?.name]]}
          pageNotice={{ tone: "lavender", title: "Quelle und Zuständigkeit", text: "Eine Organisationsbestätigung ist keine Zugangszertifizierung." }}
          description="Die Prüfung muss durch eine andere Person als die erfassende erfolgen."
          fields={[
            { name: "action", label: "Entscheidung", type: "select", required: true, options: [
              { value: "approve", label: "Freigeben" }, { value: "ask", label: "Rückfrage stellen" }, { value: "reject", label: "Ablehnen" }] },
            { name: "message", label: "Begründung / Rückfrage", type: "textarea", help: "Pflicht bei Rückfrage und Ablehnung." },
          ]}
          onSubmit={async (v) => { await api.post(`/organizations/${o.id}/decision`, v); return "Entscheidung gespeichert."; }} />
      )}
      {user?.role === "super_admin" && o.status === "freigegeben" && (
        <ActionButton label="Organisationsteam einladen" icon={UserPlus} testId="invite-org-team" onDone={reload} submitLabel="Einladung erstellen"
          page="team-einladen" title="Organisationsteam einladen" pageSubtitle="Die Einladung erteilt nur die angezeigten Organisationsrechte."
          pageSubject={o.name} pageRows={[["Organisation", o.name], ["Umfang", "Nur Rechte für diese Organisation"]]}
          fields={inviteFields([{ value: "editor", label: "Redaktion" }, { value: "support", label: "Support" }, { value: "reviewer", label: "Prüfer:in" }])}
          onSubmit={async (v) => { const { data } = await api.post("/team/invitations", { ...v, days: Number(v.days), organization_id: o.id }); return showInvite(data); }} />
      )}
    </>
  );
}

function OrgView({ d, reload }) {
  const o = d.organization;
  const [status, setStatus] = useState("all");
  const acts = d.activities.filter((a) => status === "all" || a.status === status);
  return (
    <>
      <PageHeader title={o.name} subtitle={[o.city, o.country].filter(Boolean).join(" · ") || "Ort nicht angegeben"} icon={Building2} actions={<Actions d={d} reload={reload} />} />
      <div className="grid gap-5 lg:grid-cols-2">
        <Section title="Angaben" testId="org-info">
          <Info items={[["Beschreibung", o.description], ["Website", o.website], ["Verantwortliche Stelle", o.responsibility],
            ["Quelle", o.source], ["Interne Notiz", o.internal_note], o.decision_note && ["Letzte Begründung", o.decision_note],
            ["Erfasst durch", o.created_by?.name], ["Aktualisiert", fmt(o.updated_at)]]} />
        </Section>
        <Section title="Aktivitäten der Organisation" testId="org-activities" actions={<>
          <SelectField id="org-act-status" label="Status" value={status} onChange={setStatus} testId="org-activities-status"
            options={[{ value: "all", label: "Alle" }, { value: "veröffentlicht", label: "Veröffentlicht" }, { value: "entwurf", label: "Entwurf" }, { value: "abgesagt", label: "Abgesagt" }]} />
          <div className="self-end"><OrgActivitiesPage o={o} acts={d.activities} /></div>
          <Link to={`/aktivitaeten?organisation=${encodeURIComponent(o.id)}`} data-testid="org-new-activity" className="self-end rounded-lg border border-brand-purple/30 px-3 py-2 text-sm font-semibold text-brand-purple hover:bg-secondary">Neue Aktivität</Link>
        </>}>
          {acts.length === 0 ? <Empty text="Keine Aktivitäten für diese Organisation." /> : (
            <ul className="divide-y divide-border">
              {acts.map((a) => (
                <li key={a.id} className="flex items-center justify-between gap-2 py-2.5">
                  <Link to={`/aktivitaeten/${a.id}`} className="font-medium text-brand-purple hover:underline">{a.title}</Link>
                  <span className="flex items-center gap-2 text-xs text-muted-foreground">{fmt(a.date)} <Status value={a.status} /></span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
      <div className="mt-5"><OrgLocations o={o} /></div>
      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <Section title="Einladungen des Organisationsteams" testId="org-invitations">
          {d.invitations.length === 0 ? <Empty text="Noch keine Einladungen." /> : (
            <ul className="divide-y divide-border text-sm">
              {d.invitations.map((i) => <li key={i.id} className="flex justify-between py-2"><span>{i.email}</span><Status value={i.status} /></li>)}
            </ul>
          )}
        </Section>
        <Section title="Verlauf" testId="org-history"><History items={o.history} /></Section>
      </div>
    </>
  );
}

export default function OrganizationDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const st = useLoad(`/organizations/${id}`);
  return (
    <div>
      {st.code !== "foreign_organization" && !user?.nav_menu && <BackLink to="/organisationen">Zurück zu Organisationen</BackLink>}
      <Loadable state={st}>{(d) => <OrgView d={d} reload={st.reload} />}</Loadable>
    </div>
  );
}
